export const createWebsiteStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Horsebeat.cz",
  "url": "https://horsebeat.cz",
  "description": "Dostihové syndikáty pro fanoušky i začátečníky. Podrobné statistiky koní a žokejů, kompletní databáze českých dostihů",
  "inLanguage": "cs",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://horsebeat.cz/horses?search={search_term_string}",
    "query-input": "required name=search_term_string"
  }
});

export const createOrganizationStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Horsebeat.cz",
  "url": "https://horsebeat.cz",
  "description": "Portál pro milovníky dostihů se syndikáty, statistikami a komunitou",
  "foundingDate": "2024",
  "areaServed": {
    "@type": "Country",
    "name": "Czech Republic"
  },
  "knowsAbout": [
    "Dostihové sporty",
    "Statistiky koní",
    "Dostihové kurzy",
    "Žokejové statistiky",
    "Sázení na dostihy"
  ]
});

export const createSportsEventStructuredData = (event: any) => ({
  "@context": "https://schema.org",
  "@type": "SportsEvent",
  "name": `Dostihy ${event.location} - ${event.date}`,
  "description": `Dostihový den v ${event.location}`,
  "startDate": event.date,
  "eventStatus": "https://schema.org/EventScheduled",
  "url": `https://horsebeat.cz/race-results?eventId=${event.id}`,
  "image": "https://horsebeat.cz/og-image.jpg",
  "location": {
    "@type": "Place",
    "name": event.location,
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "CZ"
    }
  },
  "sport": "Dostihový sport",
  "organizer": {
    "@type": "Organization",
    "name": event.location,
    "url": "https://horsebeat.cz"
  },
  "performer": [
    {
      "@type": "SportsTeam",
      "name": "Dostihové koně",
      "sport": "Dostihový sport"
    }
  ],
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "CZK",
    "availability": "https://schema.org/InStock",
    "description": "Sledování dostihů zdarma"
  }
});

export const createAnimalStructuredData = (horse: any) => ({
  "@context": "https://schema.org",
  "@type": "Animal",
  "name": horse.name,
  "species": "Horse",
  "description": `Dostihový kůň ${horse.name} - statistiky a historie dostihů`,
  "category": "Dostihový kůň",
  "additionalProperty": [
    {
      "@type": "PropertyValue",
      "name": "Celkem dostihů",
      "value": horse.totalRaces
    },
    {
      "@type": "PropertyValue", 
      "name": "Vítězství",
      "value": horse.wins
    },
    {
      "@type": "PropertyValue",
      "name": "Úspěšnost",
      "value": `${horse.winRate}%`
    }
  ]
});

export const createPersonStructuredData = (jockey: any) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "name": jockey.name,
  "description": `Žokej ${jockey.name} - statistiky a výsledky`,
  "jobTitle": "Žokej",
  "worksFor": {
    "@type": "Organization",
    "name": "Dostihové sporty"
  },
  "additionalProperty": [
    {
      "@type": "PropertyValue",
      "name": "Celkem dostihů",
      "value": jockey.totalRaces
    },
    {
      "@type": "PropertyValue",
      "name": "Vítězství", 
      "value": jockey.wins
    },
    {
      "@type": "PropertyValue",
      "name": "Úspěšnost",
      "value": `${jockey.winRate}%`
    }
  ]
});

export const createBreadcrumbStructuredData = (items: Array<{name: string, url: string}>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const createLocalBusinessStructuredData = (location: string) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": `Závodiště ${location}`,
  "description": `Dostihové závodiště v ${location} s pravidelnými dostihy a sázením`,
  "address": {
    "@type": "PostalAddress",
    "addressLocality": location,
    "addressCountry": "CZ"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": location === "Pardubice" ? "50.0367" : "50.0848",
    "longitude": location === "Pardubice" ? "15.7520" : "14.2386"
  },
  "telephone": "+420-XXX-XXX-XXX",
  "url": `https://horsebeat.cz/races/${location.toLowerCase().replace(' ', '-')}`,
  "openingHours": "Mo-Su 08:00-20:00",
  "priceRange": "$$"
});

export const createEventSeriesStructuredData = (location: string) => ({
  "@context": "https://schema.org",
  "@type": "EventSeries",
  "name": `Dostihové dny ${location}`,
  "description": `Pravidelné dostihové události na závodišti ${location}`,
  "location": {
    "@type": "Place",
    "name": location,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": location,
      "addressCountry": "CZ"
    }
  },
  "organizer": {
    "@type": "Organization",
    "name": `Závodiště ${location}`,
    "url": "https://horsebeat.cz"
  }
});

export const createFAQStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Co jsou dostihové kurzy?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Dostihové kurzy jsou pravděpodobnosti vítězství jednotlivých koní v dostizích, které se používají pro sázení. Čím nižší kurz, tím vyšší pravděpodobnost vítězství."
      }
    },
    {
      "@type": "Question", 
      "name": "Jak funguje fantasy liga?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Fantasy liga umožňuje hráčům vytvořit vlastní stáj koní a soutěžit o body na základě skutečných výsledků dostihů. Každý kůň má svou cenu a hráči musí hospodařit s omezeným rozpočtem."
      }
    },
    {
      "@type": "Question",
      "name": "Kde najdu statistiky koní a žokejů?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Podrobné statistiky všech koní a žokejů najdete v sekcích Koně a Žokejové. Každý profil obsahuje historii dostihů, úspěšnost, průměrné kurzy a další užitečné informace."
      }
    }
  ]
});

export const createArticleStructuredData = (article: any) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title,
  "description": article.excerpt,
  "author": {
    "@type": "Organization",
    "name": "Horsebeat.cz"
  },
  "publisher": {
    "@type": "Organization", 
    "name": "Horsebeat.cz",
    "logo": {
      "@type": "ImageObject",
      "url": "https://horsebeat.cz/og-image.jpg"
    }
  },
  "datePublished": article.published_at,
  "dateModified": article.updated_at,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `https://horsebeat.cz/blog/${article.slug}`
  },
  "image": article.featured_image || "https://horsebeat.cz/og-image.jpg"
});

export interface ProductData {
  id: string;
  name: string;
  description: string;
  price: number;
  currency?: string;
  imageUrl: string;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
  condition?: 'NewCondition' | 'UsedCondition' | 'RefurbishedCondition';
  sku?: string;
  brand?: string;
  slug?: string;
}

export const createProductStructuredData = (product: ProductData) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": product.name,
  "description": product.description?.replace(/<[^>]*>/g, '').substring(0, 500) || product.name,
  "image": product.imageUrl,
  "sku": product.sku || product.id,
  "brand": {
    "@type": "Brand",
    "name": product.brand || "Horsebeat"
  },
  "offers": {
    "@type": "Offer",
    "url": product.slug 
      ? `https://horsebeat.cz/shop/product/${product.slug}` 
      : `https://horsebeat.cz/shop`,
    "priceCurrency": product.currency || "CZK",
    "price": product.price,
    "priceValidUntil": `${new Date().getFullYear()}-12-31`,
    "availability": `https://schema.org/${product.availability || 'InStock'}`,
    "itemCondition": `https://schema.org/${product.condition || 'NewCondition'}`,
    "seller": {
      "@type": "Organization",
      "name": "Horsebeat.cz"
    },
    "hasMerchantReturnPolicy": {
      "@type": "MerchantReturnPolicy",
      "applicableCountry": "CZ",
      "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
      "merchantReturnDays": 14,
      "returnMethod": "https://schema.org/ReturnByMail",
      "returnFees": "https://schema.org/ReturnFeesCustomerResponsibility"
    },
    "shippingDetails": {
      "@type": "OfferShippingDetails",
      "shippingRate": {
        "@type": "MonetaryAmount",
        "value": 49,
        "currency": "CZK"
      },
      "shippingDestination": {
        "@type": "DefinedRegion",
        "addressCountry": "CZ"
      },
      "deliveryTime": {
        "@type": "ShippingDeliveryTime",
        "handlingTime": { "@type": "QuantitativeValue", "minValue": 1, "maxValue": 3, "unitCode": "DAY" },
        "transitTime": { "@type": "QuantitativeValue", "minValue": 5, "maxValue": 14, "unitCode": "DAY" }
      }
    }
  }
});

export const createProductListStructuredData = (products: ProductData[]) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Horsebeat Shop - Merchandise",
  "description": "Oficiální oblečení a doplňky Horsebeat pro milovníky dostihového sportu",
  "numberOfItems": products.length,
  "itemListElement": products.map((product, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "item": {
      "@type": "Product",
      "@id": `https://horsebeat.cz/shop#product-${product.id}`,
      "name": product.name,
      "description": product.description?.replace(/<[^>]*>/g, '').substring(0, 200) || product.name,
      "image": product.imageUrl,
      "sku": product.sku || product.id,
      "brand": {
        "@type": "Brand",
        "name": product.brand || "Horsebeat"
      },
      "offers": {
        "@type": "Offer",
        "priceCurrency": product.currency || "CZK",
        "price": product.price,
        "availability": `https://schema.org/${product.availability || 'InStock'}`
      }
    }
  }))
});